import { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';

import type { FetchEntry, SchemaField } from '../../data/instructions.generated';
import { useTransactionRunner, FETCH_ALL_SUPPORTED } from '../../hooks/useTransactionRunner';
import { useCodeFlavor, type CodeFlavor } from '../../lib/code-flavor-context';
import { displayInfo } from '../../lib/instruction-display';
import { Console } from './Console';
import { MinimalCodeBlock, type CodeTab } from './CodeBlock';

interface FetchPageProps {
  fetchEntry: FetchEntry;
}

type FetchMode = 'one' | 'all';

// Plural names for the fetchAll snippet — matches the SDK wrapper names exactly
// (fetchAllContracts, fetchAllConfigs, etc.) so devs can copy/paste verbatim.
// Keep in sync with FETCH_ALL_FNS in useTransactionRunner.
const PLURAL_FN: Record<string, string> = {
  borrower: 'fetchAllBorrowers',
  config: 'fetchAllConfigs',
  contract: 'fetchAllContracts',
  rental: 'fetchAllRentals',
  member: 'fetchAllMembers',
};
const PLURAL_VAR: Record<string, string> = {
  borrower: 'borrowers',
  config: 'configs',
  contract: 'contracts',
  rental: 'rentals',
  member: 'members',
};

function buildSnippet(entry: FetchEntry, flavor: CodeFlavor, mode: FetchMode): string {
  const importPath = flavor === 'kit' ? '@sly-rentals/core' : '@sly-rentals/core/legacy';
  if (mode === 'all' && PLURAL_FN[entry.acct]) {
    const fn = PLURAL_FN[entry.acct];
    const varName = PLURAL_VAR[entry.acct];
    return [`import { ${fn} } from '${importPath}';`, '', `const ${varName} = await ${fn}();`].join(
      '\n'
    );
  }
  return [
    `import { ${entry.name} } from '${importPath}';`,
    '',
    `const ${entry.acct} = await ${entry.name}('${entry.acct.toUpperCase()}_ADDRESS_HERE');`,
  ].join('\n');
}

export function FetchPage({ fetchEntry }: FetchPageProps) {
  const { flavor, setFlavor } = useCodeFlavor();
  const runner = useTransactionRunner();
  const cap = fetchEntry.acct.charAt(0).toUpperCase() + fetchEntry.acct.slice(1);

  const fetchAllSupported = FETCH_ALL_SUPPORTED.has(fetchEntry.acct);
  const [mode, setMode] = useState<FetchMode>('one');

  // Reset console state when navigating between fetches.
  useEffect(() => {
    runner.reset();
    setMode('one');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEntry.name]);

  const tabs = useMemo<CodeTab[]>(
    () => [
      { id: 'kit', label: '@solana/kit', source: buildSnippet(fetchEntry, 'kit', mode) },
      { id: 'web3js', label: '@solana/web3.js', source: buildSnippet(fetchEntry, 'web3js', mode) },
    ],
    [fetchEntry, mode]
  );

  return (
    <div className="ix-grid">
      <div>
        <div className="ix-head">
          {(() => {
            const parent = fetchEntry.group;
            const info = displayInfo(fetchEntry.name, parent);
            let display: string;
            if (parent === 'other') {
              // Outliers keep their full name; sibling-group context
              // is what makes a stripped `fetch` label readable.
              display = fetchEntry.name;
            } else {
              display = info.display;
              if (display.toLowerCase().startsWith('fetch')) {
                const rest = display.slice(5);
                display = rest ? rest.charAt(0).toLowerCase() + rest.slice(1) : 'fetch';
              }
              // Solo-fetch collapse — see Sidebar.navItemFromFetch
              // for the matching rule. Aliased fetches (fetchFiber
              // routed into the `thread/fiber` subsection) keep the
              // distinguishing token.
              const lastSeg = info.subgroup?.split('/').pop() ?? '';
              const aliased = info.subgroup && lastSeg !== fetchEntry.acct.toLowerCase();
              if (!aliased && display.toLowerCase() === fetchEntry.acct.toLowerCase()) {
                display = 'fetch';
              }
            }
            const subSegments = info.subgroup ? info.subgroup.split('/') : [];
            return (
              <div className="ix-kicker">
                <span className="br">[</span>
                <span className="group">&nbsp;{parent}</span>
                {subSegments.map(seg => (
                  <span key={seg}>
                    <span className="br">/</span>
                    <span className="group">&nbsp;{seg}</span>
                  </span>
                ))}
                <span className="br">/</span>
                <span className="name">&nbsp;{display}&nbsp;</span>
                <span className="br">]</span>
              </div>
            );
          })()}
          <h1 className="ix-title">
            {fetchEntry.name}
            <a
              href={`https://wuwei-labs.github.io/srsly/api/srsly/functions/index.${fetchEntry.name}.html`}
              target="_blank"
              rel="noopener"
              className="ix-docs-link"
              title={`Open ${fetchEntry.name} reference docs`}
            >
              docs <ExternalLink size={11} strokeWidth={1.6} />
            </a>
          </h1>
          <p className="ix-sub">
            {fetchEntry.description || (
              <>
                Decode an on-chain <span className="mono cyan">{cap}</span> account into a typed JS
                object. PDAs come pre-derived, ATLAS amounts are denominated, and enums are returned
                by name.
              </>
            )}
          </p>
        </div>

        <h2 className="ix-section-label">Returns {mode === 'all' ? `${cap}[]` : cap}</h2>
        <div className="schema">
          {fetchEntry.schema ? (
            <SchemaTable fields={fetchEntry.schema} />
          ) : (
            <div className="schema-row" style={{ paddingTop: 12 }}>
              <p className="schema-desc">
                Run a fetch on the right to see the full shape. Calling on a non-existent or
                wrong-typed address surfaces as <span className="mono">notfound</span>.
              </p>
            </div>
          )}
        </div>
      </div>

      <aside
        style={{
          position: 'sticky',
          top: 24,
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <h2 className="ix-section-label" style={{ marginBottom: 0, flex: 1 }}>
            Live preview
          </h2>
          {fetchAllSupported && (
            <div className="net-pill" role="tablist" aria-label="Fetch mode">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'one'}
                className={mode === 'one' ? 'is-active' : ''}
                onClick={() => {
                  setMode('one');
                  runner.reset();
                }}
              >
                One
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'all'}
                className={mode === 'all' ? 'is-active' : ''}
                onClick={() => {
                  setMode('all');
                  runner.reset();
                }}
              >
                All
              </button>
            </div>
          )}
        </div>
        <MinimalCodeBlock
          tabs={tabs}
          active={flavor}
          onActiveChange={id => setFlavor(id as CodeFlavor)}
        />
        <Console
          key={mode}
          mode={mode === 'all' ? 'fetch-all' : 'fetch-only'}
          accountType={fetchEntry.acct}
          status={runner.status}
          onReset={runner.reset}
          onFetch={runner.fetchAccount}
          onFetchAll={runner.fetchAllAccounts}
        />
      </aside>
    </div>
  );
}

// Per-field rows showing the decoded shape. Reuses the existing `.schema-row`
// styling from instruction params so the visual hierarchy matches between
// instruction-page params and fetch-page returns.
function SchemaTable({ fields }: { fields: SchemaField[] }) {
  return (
    <>
      {fields.map(f => (
        <div key={f.name} className="schema-row">
          <div className="schema-meta">
            <span className="schema-name">{f.name}</span>
            <span className="schema-type">{f.type}</span>
          </div>
          {f.docs && <p className="schema-desc">{f.docs}</p>}
        </div>
      ))}
    </>
  );
}
